import { z } from 'zod';
import type { ApiSchemaClass, ApiSchemaDefinition } from './types';
import {
  datasetColumnDisplayOptionsSchema,
  datasetResourceResponseSchema,
} from './common-responses';

export const datasetColumnValueV20260601Schema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
]);

/**
 * Logical column types a caller can declare. They map onto DuckDB types when
 * the artifact is written, which is why the same set is used by `add_column`
 * and by manual dataset creation.
 */
export const datasetColumnTypeV20260601Schema = z.enum([
  'string',
  'number',
  'boolean',
  'date',
]);

export const datasetOperationV20260601Schema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('update_cell'),
    rowId: z.string().min(1),
    /**
     * The row's durable business key, when it has one.
     *
     * Carried alongside `rowId` rather than instead of it because the two serve
     * different readers. `rowId` is the artifact's own row number, which lets
     * the grid join an operation onto rows it already holds without resolving
     * anything. This is what the fold joins on, and it stays correct when the
     * artifact is a sample of a larger dataset, or when row numbers have moved.
     */
    rowKey: z.string().min(1).nullable().optional(),
    columnName: z.string().min(1),
    value: datasetColumnValueV20260601Schema,
  }),
  z.object({
    type: z.literal('insert_row'),
    rowId: z.string().min(1).optional(),
    values: z.record(z.string(), datasetColumnValueV20260601Schema),
    position: z
      .object({
        afterRowId: z.string().min(1).nullable(),
      })
      .optional(),
  }),
  z.object({
    type: z.literal('delete_row'),
    rowId: z.string().min(1),
    /** See `rowKey` on `update_cell`. */
    rowKey: z.string().min(1).nullable().optional(),
  }),
  z.object({
    type: z.literal('add_column'),
    columnName: z.string().min(1),
    columnType: datasetColumnTypeV20260601Schema,
    defaultValue: datasetColumnValueV20260601Schema.optional(),
  }),
  z.object({
    type: z.literal('rename_column'),
    oldColumnName: z.string().min(1),
    newColumnName: z.string().min(1),
  }),
  z.object({
    type: z.literal('delete_column'),
    columnName: z.string().min(1),
  }),
]);

export const datasetOperationResponseV20260601Schema = z.object({
  id: z.string().uuid(),
  ownerOrgId: z.string().uuid(),
  datasetId: z.string().uuid(),
  baseArtifactId: z.string().uuid().nullable(),
  sequenceNumber: z.number().int().nonnegative(),
  clientOperationId: z.string().nullable(),
  // Null for `origin: 'source'` operations — changes we observed in the source
  // warehouse have no user author.
  actorId: z.string().uuid().nullable(),
  type: z.enum([
    'update_cell',
    'insert_row',
    'delete_row',
    'add_column',
    'rename_column',
    'delete_column',
  ]),
  payload: datasetOperationV20260601Schema,
  compactedAt: z.string().datetime().nullable(),
  compactedIntoArtifactId: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const datasetOperationBatchBodyV20260601Schema = z.object({
  baseArtifactId: z.string().uuid().nullable(),
  operations: z
    .array(
      z.object({
        clientOperationId: z.string().min(1),
        op: datasetOperationV20260601Schema,
      })
    )
    .min(1)
    .max(500),
});

export const datasetOperationBatchResponseV20260601Schema = z.object({
  operations: z.array(
    z.object({
      clientOperationId: z.string(),
      operationId: z.string().uuid(),
      sequenceNumber: z.number().int().nonnegative(),
      rowId: z.string().optional(),
    })
  ),
});

export const datasetOperationListResponseV20260601Schema = z.object({
  operations: z.array(datasetOperationResponseV20260601Schema),
});

export type DatasetColumnValueV20260601 = z.infer<
  typeof datasetColumnValueV20260601Schema
>;

export type DatasetOperationV20260601 = z.infer<
  typeof datasetOperationV20260601Schema
>;

export type DatasetOperationResponseV20260601 = z.infer<
  typeof datasetOperationResponseV20260601Schema
>;

export type DatasetOperationBatchBodyV20260601 = z.infer<
  typeof datasetOperationBatchBodyV20260601Schema
>;

export type DatasetOperationBatchResponseV20260601 = z.infer<
  typeof datasetOperationBatchResponseV20260601Schema
>;

export type DatasetOperationListResponseV20260601 = z.infer<
  typeof datasetOperationListResponseV20260601Schema
>;

export const datasetCsvCreateBodyV20260601Schema = z.object({
  name: z
    .string()
    .min(1)
    .optional()
    .describe(
      'Optional dataset display name. Defaults to the uploaded filename.'
    ),
  folderId: z
    .string()
    .uuid()
    .optional()
    .describe(
      'Optional folder UUID to place the new dataset in. When omitted, the dataset is created in the caller organization root.'
    ),
});

export type DatasetCsvCreateBody = z.infer<
  typeof datasetCsvCreateBodyV20260601Schema
>;

export class DatasetCsvCreateV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'dataset-csv-create-2026-06-01' as const;

  readonly name = DatasetCsvCreateV20260601Schema.version;
  readonly schema = datasetCsvCreateBodyV20260601Schema;
  readonly response = datasetResourceResponseSchema;
}

/**
 * Creates the dataset a CSV is about to be uploaded into, before its bytes are
 * sent. The file's own details are declared here because the dataset takes its
 * name and size from them and they are known before the upload starts.
 *
 * The one-shot create carries the file and so cannot answer until the last byte
 * has landed — which on a large file is a long time to hold a caller with
 * nothing to show. This answers immediately, and the upload that follows goes
 * to the dataset's own import route, where its progress belongs to a dataset
 * that already exists.
 */
export const datasetCsvCreatePendingBodyV20260601Schema = z.object({
  name: z
    .string()
    .min(1)
    .optional()
    .describe(
      'Optional dataset display name. Defaults to the uploaded filename.'
    ),
  folderId: z
    .string()
    .uuid()
    .optional()
    .describe(
      'Optional folder UUID to place the new dataset in. When omitted, the dataset is created in the caller organization root.'
    ),
  originalFilename: z
    .string()
    .min(1)
    .max(255)
    .optional()
    .describe('Name of the file being uploaded, used as the default name.'),
  mimeType: z
    .string()
    .min(1)
    .max(255)
    .optional()
    .describe('Content type the browser reported for the file, when it did.'),
  sizeBytes: z
    .number()
    .int()
    .nonnegative()
    .optional()
    .describe('Size of the file being uploaded, in bytes.'),
  sourceChatId: z
    .string()
    .uuid()
    .optional()
    .describe(
      'When set, the dataset is created as a chat-scoped upload: hidden from the library until promoted, and stamped with the chat it came from. Used by Coco chat file uploads.'
    ),
});

export type DatasetCsvCreatePendingBody = z.infer<
  typeof datasetCsvCreatePendingBodyV20260601Schema
>;

export class DatasetCsvCreatePendingV20260601Schema
  implements ApiSchemaDefinition
{
  static readonly version = 'dataset-csv-create-pending-2026-06-01' as const;

  readonly name = DatasetCsvCreatePendingV20260601Schema.version;
  readonly schema = datasetCsvCreatePendingBodyV20260601Schema;
  readonly response = datasetResourceResponseSchema;
}

/**
 * Creates a dataset whose rows are authored in the product rather than
 * uploaded. Everything is optional: the default flow creates an empty dataset
 * and the user then adds columns in the grid or imports a CSV into it.
 */
export const datasetManualCreateBodyV20260601Schema = z.object({
  name: z
    .string()
    .min(1)
    .max(255)
    .optional()
    .describe('Optional dataset display name. Defaults to "Untitled dataset".'),
  folderId: z
    .string()
    .uuid()
    .optional()
    .describe(
      'Optional folder UUID to place the new dataset in. When omitted, the dataset is created in the caller organization root.'
    ),
  columns: z
    .array(
      z.object({
        name: z.string().min(1).max(255),
        type: datasetColumnTypeV20260601Schema,
      })
    )
    .max(200)
    .optional()
    .describe(
      'Declared columns, in display order. Names must be unique. Omitted or empty creates a dataset with no columns yet, to be populated in the grid or by importing a CSV.'
    ),
  rows: z
    .array(z.record(z.string(), datasetColumnValueV20260601Schema))
    .max(1000)
    .optional()
    .describe(
      'Optional seed rows keyed by column name. Unknown keys are rejected; missing keys are written as null.'
    ),
});

export type DatasetManualCreateBody = z.infer<
  typeof datasetManualCreateBodyV20260601Schema
>;

export class DatasetManualCreateV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'dataset-manual-create-2026-06-01' as const;

  readonly name = DatasetManualCreateV20260601Schema.version;
  readonly schema = datasetManualCreateBodyV20260601Schema;
  readonly response = datasetResourceResponseSchema;
}

/**
 * Creates a dataset seeded from a SELECT run against the org's connected
 * warehouse. The row is created immediately at `importStatus: 'importing'`; a
 * saga then runs the query, writes the capped parquet preview and settles it to
 * `ready`, exactly like a CSV upload — so the caller mints the id, opens the
 * page, and watches the same import progress.
 */
export const datasetWarehouseCreateBodyV20260601Schema = z.object({
  sourceQuery: z
    .string()
    .min(1)
    .max(100_000)
    .describe(
      'The read-only SELECT to seed the dataset from, run against the org warehouse. The parquet preview holds the first rows of its result.'
    ),
  rowKeyColumns: z
    .array(z.string().min(1))
    .min(1)
    .max(16)
    .describe(
      'Columns of the query result that identify a row, in order. Required for warehouse datasets: the preview is a capped sample and the table changes upstream, so a stable business key is what lets change detection and preview top-ups match rows across scans.'
    ),
  name: z
    .string()
    .min(1)
    .max(255)
    .optional()
    .describe('Optional dataset display name. Defaults to "Untitled dataset".'),
  folderId: z
    .string()
    .uuid()
    .optional()
    .describe(
      'Optional folder UUID to place the new dataset in. When omitted, the dataset is created in the caller organization root.'
    ),
  cadence: z
    .string()
    .min(1)
    .max(255)
    .optional()
    .describe(
      'Cron for how often the dataset is re-scanned for warehouse changes. A warehouse dataset can change upstream with no event, so a scan is how those changes are seen.'
    ),
  cadenceTimezone: z
    .string()
    .min(1)
    .max(255)
    .optional()
    .describe(
      'IANA timezone the cadence cron is interpreted in. Defaults to UTC.'
    ),
});

export type DatasetWarehouseCreateBody = z.infer<
  typeof datasetWarehouseCreateBodyV20260601Schema
>;

export class DatasetWarehouseCreateV20260601Schema
  implements ApiSchemaDefinition
{
  static readonly version = 'dataset-warehouse-create-2026-06-01' as const;

  readonly name = DatasetWarehouseCreateV20260601Schema.version;
  readonly schema = datasetWarehouseCreateBodyV20260601Schema;
  readonly response = datasetResourceResponseSchema;
}

/**
 * Re-runs an existing warehouse dataset's seed with a new SELECT — the retry a
 * failed seed offers, so the user can fix the query without creating a fresh
 * dataset. Replaces the stored `sourceQuery` and kicks the seed saga again.
 */
export const datasetWarehouseReseedBodyV20260601Schema = z.object({
  sourceQuery: z
    .string()
    .min(1)
    .max(100_000)
    .describe('The SELECT to re-seed the dataset from.'),
});

export type DatasetWarehouseReseedBody = z.infer<
  typeof datasetWarehouseReseedBodyV20260601Schema
>;

export class DatasetWarehouseReseedV20260601Schema
  implements ApiSchemaDefinition
{
  static readonly version = 'dataset-warehouse-reseed-2026-06-01' as const;

  readonly name = DatasetWarehouseReseedV20260601Schema.version;
  readonly schema = datasetWarehouseReseedBodyV20260601Schema;
  readonly response = datasetResourceResponseSchema;
}

/**
 * Saves dataset-level settings. Every field is optional and only the ones
 * present are written, so callers can rename without touching the query and
 * vice versa. None of this changes the stored rows, which is why it is a plain
 * update rather than a dataset operation.
 */
export const datasetSettingsBodyV20260601Schema = z.object({
  name: z
    .string()
    .min(1)
    .max(255)
    .optional()
    .describe('New dataset display name. Omit to leave the name unchanged.'),
  previewQuery: z
    .string()
    .max(10_000)
    .nullable()
    .optional()
    .describe(
      'Read-only SELECT the preview reads through, where `dataset` refers to the dataset itself. Null restores the default, omitted leaves it unchanged.'
    ),
  rowKeyColumns: z
    .array(z.string().min(1))
    .max(16)
    .nullable()
    .optional()
    .describe(
      'Key columns identifying a row, in order. Every name must be a column of the latest artifact, and the only internal column accepted is that artifact’s own row id — which a CSV dataset is declared with at import, and which does not survive a re-import. Null or an empty array clears the declaration, falling back to the same row id implicitly. Omitted leaves it unchanged.'
    ),
  expandedAutomationWorkflowIds: z
    .array(z.string().min(1))
    .max(64)
    .nullable()
    .optional()
    .describe(
      'Ids of attached workflows to expand into columns showing each one’s most-recent run output for a row. Null or an empty array expands nothing. Omitted leaves it unchanged. Ids that no longer name a bound workflow are ignored when the grid renders.'
    ),
  cadence: z
    .string()
    .max(255)
    .nullable()
    .optional()
    .describe(
      'Cron expression driving scheduled change detection for this dataset. Each tick re-evaluates the dataset against its watchers. Null clears it (push-only, evaluated only when a write announces a change). Omitted leaves it unchanged.'
    ),
  cadenceTimezone: z
    .string()
    .max(64)
    .optional()
    .describe(
      'IANA timezone the cadence cron is interpreted in. Defaults to UTC. Omitted leaves it unchanged.'
    ),
  writeBackTable: z
    .object({
      schema: z.string().trim().min(1).max(255),
      name: z.string().trim().min(1).max(255),
    })
    .nullable()
    .optional()
    .describe(
      'Warehouse table workflow write-backs land in, named exactly as the warehouse stores it. Must exist and contain the dataset’s row-key columns. Warehouse datasets only. Null falls back to the table a plain single-table source query reads; omitted leaves it unchanged.'
    ),
});

export type DatasetSettingsBodyV20260601 = z.infer<
  typeof datasetSettingsBodyV20260601Schema
>;

export class DatasetSettingsV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'dataset-settings-2026-06-01' as const;

  readonly name = DatasetSettingsV20260601Schema.version;
  readonly schema = datasetSettingsBodyV20260601Schema;
  readonly response = datasetResourceResponseSchema;
}

/**
 * Asks whether a candidate business key would identify every row, before
 * declaring it through the settings route.
 *
 * A question for the server rather than the caller because only the dataset's
 * kind knows where its rows live and how they are keyed — and it has to resolve
 * the key exactly as the trigger evaluator does, or the answer is about a
 * different key than the one that will be used.
 */
export const datasetRowKeyCheckBodyV20260601Schema = z.object({
  rowKeyColumns: z
    .array(z.string().min(1))
    .min(1)
    .max(16)
    .describe(
      'Candidate key columns, in order. Order matters: a compound key encodes its columns positionally. The artifact’s own row id is a valid candidate and is unique by construction; every other internal column is rejected.'
    ),
});

export type DatasetRowKeyCheckBodyV20260601 = z.infer<
  typeof datasetRowKeyCheckBodyV20260601Schema
>;

export const datasetRowKeyCheckResponseV20260601Schema = z.object({
  totalRows: z.number().int().nonnegative(),
  unidentifiableRows: z
    .number()
    .int()
    .nonnegative()
    .describe(
      'Rows with a null in at least one key column. These carry no identity: a trigger skips them and no run can link back to them.'
    ),
  distinctKeys: z.number().int().nonnegative(),
  duplicateRows: z
    .number()
    .int()
    .nonnegative()
    .describe(
      'Identifiable rows beyond the first for their key — rows that would be folded onto another row’s identity.'
    ),
  examples: z
    .array(
      z.object({
        values: z.array(z.string()),
        rowCount: z.number().int().nonnegative(),
      })
    )
    .describe('A bounded sample of colliding keys, for naming offenders.'),
  automations: z
    .object({
      watchingWorkflows: z
        .number()
        .int()
        .nonnegative()
        .describe(
          'Distinct workflows bound to a live trigger on this dataset.'
        ),
      rowsWithRuns: z
        .number()
        .int()
        .nonnegative()
        .describe(
          'Rows with at least one recorded run. These are the rows a key change carries over, so they will not fire again; the rest of the dataset has never been worked and may.'
        ),
    })
    .describe(
      'What declaring this key would do to the dataset’s automations. Answered by the same two relations the re-key itself reads, so the dialog cannot promise one thing and the saga do another. Independent of the candidate key: it describes what has already been worked, not what the key would identify.'
    ),
});

export type DatasetRowKeyCheckResponseV20260601 = z.infer<
  typeof datasetRowKeyCheckResponseV20260601Schema
>;

export class DatasetRowKeyCheckV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'dataset-row-key-check-2026-06-01' as const;

  readonly name = DatasetRowKeyCheckV20260601Schema.version;
  readonly schema = datasetRowKeyCheckBodyV20260601Schema;
  readonly response = datasetRowKeyCheckResponseV20260601Schema;
}

export class DatasetOperationsBatchV20260601Schema
  implements ApiSchemaDefinition
{
  static readonly version = 'dataset-operations-batch-2026-06-01' as const;

  readonly name = DatasetOperationsBatchV20260601Schema.version;
  readonly schema = datasetOperationBatchBodyV20260601Schema;
  readonly response = datasetOperationBatchResponseV20260601Schema;
}

export type DatasetColumnDisplayOptionsV20260601 = z.infer<
  typeof datasetColumnDisplayOptionsSchema
>;

/**
 * Upserts per-column display options. Each column named is written; columns not
 * listed are left untouched, so a client can persist just the columns it
 * changed. Pure presentation that never touches the stored rows — which is why
 * it is a plain settings-style update rather than a dataset operation — and
 * shared across everyone who opens the dataset.
 */
export const datasetColumnSettingsBodyV20260601Schema = z.object({
  columns: z
    .array(
      z.object({
        columnName: z.string().min(1),
        displayOptions: datasetColumnDisplayOptionsSchema,
      })
    )
    .max(500)
    .describe('Per-column display options to upsert.'),
});

export type DatasetColumnSettingsBodyV20260601 = z.infer<
  typeof datasetColumnSettingsBodyV20260601Schema
>;

export class DatasetColumnSettingsV20260601Schema
  implements ApiSchemaDefinition
{
  static readonly version = 'dataset-column-settings-2026-06-01' as const;

  readonly name = DatasetColumnSettingsV20260601Schema.version;
  readonly schema = datasetColumnSettingsBodyV20260601Schema;
  readonly response = datasetResourceResponseSchema;
}

/**
 * Promotes a chat-scoped dataset (created from a file dropped into a chat) into
 * a real library dataset: it stops being hidden from the library. Optionally
 * renames it in the same step, since the chat upload defaults the name to the
 * filename.
 */
export const datasetPromoteBodyV20260601Schema = z.object({
  name: z
    .string()
    .min(1)
    .max(255)
    .optional()
    .describe('Optional new display name to give the dataset as it is saved.'),
});

export type DatasetPromoteBody = z.infer<
  typeof datasetPromoteBodyV20260601Schema
>;

export class DatasetPromoteV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'dataset-promote-2026-06-01' as const;

  readonly name = DatasetPromoteV20260601Schema.version;
  readonly schema = datasetPromoteBodyV20260601Schema;
  readonly response = datasetResourceResponseSchema;
}

export const DATASET_API_SCHEMAS = [
  DatasetColumnSettingsV20260601Schema,
  DatasetCsvCreateV20260601Schema,
  DatasetCsvCreatePendingV20260601Schema,
  DatasetManualCreateV20260601Schema,
  DatasetWarehouseCreateV20260601Schema,
  DatasetWarehouseReseedV20260601Schema,
  DatasetOperationsBatchV20260601Schema,
  DatasetRowKeyCheckV20260601Schema,
  DatasetSettingsV20260601Schema,
  DatasetPromoteV20260601Schema,
] as const satisfies readonly ApiSchemaClass[];
